import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { UserAvatar } from "common/elements";
import { btnDelay, usePlaySound } from "common/utils";
import { AccountType } from "features/user/types";
import { GuideTargetId } from "features/builder/PieceBuilderTopGuideOverlay";
import { DEFAULT_ENABLED_COMMANDS, PieceRankingEntry } from "features/builder/types";
import { MapDetailCommandPanel } from "features/builder/elements/MapDetailCommandPanel";
import { MapDetailCommentPanel } from "features/builder/elements/MapDetailCommentPanel";
import { IconBad, IconFun, IconCool, IconAmazing, IconDifficult } from "features/reaction/asset";
import {
  MockPiece,
  PieceListContext,
  PieceCardAction,
  PieceReaction,
} from "features/builder/pieceList";
import {
  reportPiece,
  reactToPiece,
  getPieceDetail,
  getPieceHints,
  getPieceRanking,
} from "features/builder/api";
import { PieceAuthor } from "./PieceAuthor";
import { BuilderModalAccent } from "./BuilderModalPanel";
import { BuilderReportDialog } from "./BuilderReportDialog";
import { BuilderAccountRequiredDialog } from "./BuilderAccountRequiredDialog";
import { StatusBadge, PieceStatusBadges, getCardVariant, minePublishBtn } from "./MapCard";
import {
  PillSize,
  PillColor,
  BuilderPillButton,
  PILL_COLOR_BY_BTN_CLASS,
} from "./BuilderPillButton";

const FALLBACK_IMAGE = `${Constants.assetHost}/assets/images/builder_default_display.webp`;

enum DetailTab {
  INFO = "info",
  RANKING = "ranking",
  COMMAND = "command",
  COMMENT = "comment",
}

const TAB_ITEMS: { id: DetailTab; labelKey: string; icon: string }[] = [
  { id: DetailTab.INFO, labelKey: "MSG_BUILDER_MAP_DETAIL_BTN", icon: "info" },
  { id: DetailTab.COMMAND, labelKey: "MSG_BUILDER_DETAIL_TAB_COMMAND", icon: "extension" },
  { id: DetailTab.RANKING, labelKey: "MSG_BUILDER_DETAIL_TAB_RANKING", icon: "leaderboard" },
  { id: DetailTab.COMMENT, labelKey: "MSG_BUILDER_DETAIL_TAB_COMMENT", icon: "chat_bubble" },
];

// 1〜3位のメダル
const RANK_MEDALS = ["👑", "🥈", "🥉"];
const HINT_LEVELS = [
  "MSG_BUILDER_DETAIL_HINT_LV1",
  "MSG_BUILDER_DETAIL_HINT_LV2",
  "MSG_BUILDER_DETAIL_HINT_LV3",
];

// UI 確認用ダミーヒント（本来は API から取得。INFO タブ内で読み取り専用表示）

// リアクション表示（アイコン・順序は creator の ReactionPanel と同じ。easy のみ PNG 画像）
const REACTIONS: { key: keyof PieceReaction; icon: JSX.Element }[] = [
  { key: "difficult", icon: <IconDifficult width={40} height={40} /> },
  {
    key: "easy",
    icon: (
      <img
        alt="*"
        draggable={false}
        className="w-10 h-10"
        src={`${Constants.assetHost}/assets/images/reaction_easy.png`}
      />
    ),
  },
  { key: "cool", icon: <IconCool width={40} height={40} /> },
  { key: "fun", icon: <IconFun width={40} height={40} /> },
  { key: "amazing", icon: <IconAmazing width={40} height={40} /> },
  { key: "bad", icon: <IconBad width={40} height={40} /> },
];

export const MapDetailPage = ({
  piece,
  initialTab,
  context,
  onBack,
  onAction,
}: {
  piece: MockPiece;
  /** メッセージのリンクから開いたときに、最初に見せるタブ */
  initialTab?: string;
  context: PieceListContext;
  onBack: () => void;
  onAction: (action: PieceCardAction) => void;
}) => {
  const play = usePlaySound();
  const active = useSelector((state: RootState) => state.user.active);
  const [tab, setTab] = useState<DetailTab>(
    (Object.values(DetailTab) as string[]).includes(initialTab ?? "")
      ? (initialTab as DetailTab)
      : DetailTab.INFO,
  );
  // お気に入りは mock 段階。即時の見た目反映用にローカル状態を持つ（実永続化は後続フェーズ）
  const [liked, setLiked] = useState(piece.is_liked);
  const [likeCount, setLikeCount] = useState(piece.liked);
  const [reportOpen, setReportOpen] = useState(false);
  const [showReportDenied, setShowReportDenied] = useState(false);
  const [ranking, setRanking] = useState<PieceRankingEntry[]>([]);
  const [hints, setHints] = useState<string[]>(["", "", ""]);

  // ヒント・ランキングは API から取得する
  useEffect(() => {
    if (!piece.piece_id) return;
    let canceled = false;
    getPieceHints({ piece_id: piece.piece_id })
      .then((res) => {
        if (!canceled) setHints(res.hints ?? ["", "", ""]);
      })
      .catch(() => undefined);
    getPieceRanking({ piece_id: piece.piece_id, limit: 10 })
      .then((rows) => {
        if (!canceled) setRanking(rows);
      })
      .catch(() => undefined);
    return () => {
      canceled = true;
    };
  }, [piece.piece_id]);
  const isOfficial = piece.is_official;

  // ピース6種リアクション（公開ピースで操作可。カウントと自分の選択を保持）
  const EMPTY_REACTION: PieceReaction = {
    difficult: 0,
    easy: 0,
    cool: 0,
    fun: 0,
    amazing: 0,
    bad: 0,
  };
  const [reactionCounts, setReactionCounts] = useState<PieceReaction>(
    piece.reaction ?? EMPTY_REACTION,
  );
  const [myReactions, setMyReactions] = useState<string[]>(piece.my_reactions ?? []);
  const canReact = context === PieceListContext.PUBLIC && !!active?.uid;

  // 公開ピースを開いたら最新のカウントと自分のリアクションを取得
  useEffect(() => {
    if (context !== PieceListContext.PUBLIC) return;
    getPieceDetail({ piece_id: piece.piece_id, uid: active?.uid })
      .then((d) => {
        if (d.reaction) setReactionCounts(d.reaction);
        setMyReactions(d.my_reactions ?? []);
      })
      .catch(() => undefined);
  }, [piece.piece_id, active?.uid, context]);

  const handleReact = (key: keyof PieceReaction) => () => {
    if (!canReact || !active?.uid) return;
    play();
    const mine = myReactions.includes(key);
    // 楽観更新
    setMyReactions((prev) => (mine ? prev.filter((k) => k !== key) : [...prev, key]));
    setReactionCounts((prev) => ({ ...prev, [key]: Math.max(0, prev[key] + (mine ? -1 : 1)) }));
    reactToPiece({ uid: active.uid, piece_id: piece.piece_id, reaction: key })
      .then((res) => {
        if (res.reactions) setReactionCounts(res.reactions);
      })
      .catch(() => {
        // 失敗したら戻す
        setMyReactions((prev) => (mine ? [...prev, key] : prev.filter((k) => k !== key)));
        setReactionCounts((prev) => ({
          ...prev,
          [key]: Math.max(0, prev[key] + (mine ? 1 : -1)),
        }));
        toast.warn(
          <div>
            <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_ACTION_FAILED")}</p>
          </div>,
        );
      });
  };

  const accentBg =
    context === PieceListContext.MINE
      ? "bg-green-100"
      : context === PieceListContext.TRASH
        ? "bg-brown-80"
        : "bg-blue-100";
  const accentBorder =
    context === PieceListContext.MINE
      ? "border-green-100"
      : context === PieceListContext.TRASH
        ? "border-brown-80"
        : "border-blue-100";

  const fire = (action: PieceCardAction) => {
    play();
    btnDelay(() => onAction(action));
  };

  const handleBack = () => {
    play();
    btnDelay(() => onBack());
  };

  const handleSelectTab = (id: DetailTab) => () => {
    if (id === tab) return;
    play();
    btnDelay(() => setTab(id));
  };

  const handleFavorite = () => {
    play();
    setLikeCount((c) => (liked ? c - 1 : c + 1));
    setLiked((v) => !v);
    onAction(PieceCardAction.LIKE);
  };

  const handleReport = () => {
    play();
    // ゲストは通報できない。API を叩いてから断るのではなく、押した時点で理由を出す
    if (active?.account_type === AccountType.GUEST) {
      setShowReportDenied(true);
      return;
    }
    setReportOpen(true);
  };

  // 名前の横のバッジ（みんなのピースは オリジナル/コピー、マイピースは 状態）
  const publicPill =
    context === PieceListContext.PUBLIC ? getCardVariant(piece, context).statusPill : null;

  // あそぶ の右（狭いときは 2 列目）に並べるボタンがあるか。ゴミばこでコピー不可だと 1 つも無い
  const hasSecondaryActions =
    context === PieceListContext.MINE || context === PieceListContext.PUBLIC || piece.enable_copied;

  // ボードのタブの上に並べる数字。みんなのピースは いいね を下のボタンに出すので、ここには入れない
  const stats: { key: string; icon: string; colorClass: string; filled: boolean; value: number }[] =
    [
      {
        key: "viewed",
        icon: "visibility",
        colorClass: "text-[#4c9df1]",
        filled: false,
        value: piece.viewed,
      },
      ...(context === PieceListContext.PUBLIC
        ? []
        : [
            {
              key: "liked",
              icon: "favorite",
              colorClass: "text-[#fc6560]",
              filled: true,
              value: piece.liked,
            },
          ]),
      {
        key: "played",
        icon: "play_arrow",
        colorClass: "text-green-100",
        filled: true,
        value: piece.played,
      },
      {
        key: "cleared",
        icon: "military_tech",
        colorClass: "text-[#2bdc98]",
        filled: false,
        value: piece.cleared,
      },
      {
        key: "copied",
        icon: "cached",
        colorClass: "text-purple",
        filled: false,
        value: piece.copied,
      },
    ];

  return (
    <div className="flex-col-el relative w-full h-full p-4 gap-4">
      {/* つかいかた は一覧ページのタイトルの帯の右上（PieceBuilderListPage）にある */}

      {/* 左にプレビュー（主役）と操作ボタン、右に タブのボード */}
      <div className="flex-row-el flex-1 w-full gap-6 min-h-0">
        <div className="@container flex-col-el flex-1 min-w-0 min-h-0 gap-4">
          {/* プレビュー。左上に名前、左下に作者、右下にピースの操作（公開・ゴミばこ／ほうこく）を重ねる。
                絵は切らずに収め（object-contain）、余る左右はキャプチャの背景色（captureThumbnail の #545454）で埋める。
                全画面ではプレビュー自体が大きいので、タップで画像だけを拡大する画面は出さない */}
          <div
            className={`flex-col-el relative flex-1 min-h-0 w-full box-border rounded-3xl overflow-hidden bg-[#545454] border-solid border-8 ${
              isOfficial
                ? "border-blue-100 shadow-[0_0_12px_rgba(76,157,241,0.4)]"
                : "border-textcolor-black"
            }`}
          >
            <img
              alt={piece.name}
              src={piece.cover ?? piece.capture ?? FALLBACK_IMAGE}
              onError={(e) => {
                (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
              }}
              className="w-full h-full object-contain"
            />

            {/* 名前のプレート（カード選択のカードと同じ、色の帯＋アイコン＋名前）。
                状態のバッジはボードの一番上に置き、名前に幅を残す。
                右上の ゴミばこへ／ほうこく（48 ＋右の余白 16 ＋すき間）にかからない幅までにする */}
            <div
              className={`flex-row-el items-center absolute top-6 left-0 ${
                context === PieceListContext.TRASH ? "max-w-[92%]" : "max-w-[calc(100%-96px)]"
              }`}
            >
              <div className={`w-4 h-16 shrink-0 ${accentBg}`} />
              <div className="flex-row-el items-center gap-3 h-16 min-w-0 pr-6 bg-white/90 rounded-r-2xl">
                <div className={`flex-col-center shrink-0 w-16 h-16 rounded-br-2xl ${accentBg}`}>
                  <span className="material-symbols-outlined type-title text-white">extension</span>
                </div>
                {/* type-text は white-space: pre-line と flex-shrink: 0 を持つので、! で上書きして 1 行で切る */}
                <p className="type-text type-title !shrink min-w-0 truncate !whitespace-nowrap text-textcolor-black">
                  {piece.name}
                </p>
              </div>
            </div>

            {/* 右上：マイピースは ゴミばこへ、みんなのピースは ほうこく（どちらもアイコンだけ。押すと確認のダイアログが出る）。
                高さは名前のプレート（上 24・高さ 64）の真ん中にそろえる */}
            {context !== PieceListContext.TRASH && (
              <div
                data-guide="detail_corner"
                className="flex-row-el items-center absolute top-8 right-4"
              >
                {context === PieceListContext.MINE && (
                  <BuilderPillButton
                    color={PillColor.RED}
                    size={PillSize.SM}
                    icon="delete"
                    ariaLabel={I18n.t("MSG_BUILDER_MENU_TO_TRASH")}
                    onClick={() => fire(PieceCardAction.TO_TRASH)}
                  />
                )}
                {context === PieceListContext.PUBLIC && (
                  <BuilderPillButton
                    color={PillColor.WHITE_RED}
                    size={PillSize.SM}
                    icon="flag"
                    ariaLabel={I18n.t("MSG_BUILDER_MENU_REPORT")}
                    onClick={handleReport}
                  />
                )}
              </div>
            )}

            {/* 右下：マイピースの 公開（子どもに大事な操作なので文字を残す）。
                処分されたピースは公開できない。カードと同じく公開ボタンを出さない。
                左下の作者（幅 150 ほど）と並んでも、4:3 のプレビュー（中の幅 608）に収まる */}
            {context === PieceListContext.MINE &&
              !piece.banned &&
              (() => {
                const pub = minePublishBtn(piece);
                return (
                  <div className="flex-row-el items-center absolute right-4 bottom-4">
                    <BuilderPillButton
                      color={PILL_COLOR_BY_BTN_CLASS[pub.className] ?? PillColor.GRAY}
                      size={PillSize.SM}
                      icon={pub.icon}
                      label={I18n.t(pub.labelKey)}
                      ariaLabel="toggle-publish"
                      disabled={pub.disabled}
                      onClick={() => fire(PieceCardAction.TOGGLE_PUBLISH)}
                    />
                  </div>
                );
              })()}

            {/* 作者（コピー作品はオリジナル作者→現作者） */}
            {piece.author_name && (
              // flex-col-center は position: relative を持つので !absolute で上書きする
              <div className="flex-col-center !absolute left-4 bottom-4 px-4 py-2 bg-white/90 rounded-2xl">
                <PieceAuthor piece={piece} />
              </div>
            )}
          </div>

          {/* 操作ボタン（もどる → あそぶ → へんしゅう → コピー → いいね）。高さは全部そろえ、
                いちばん押してほしい あそぶ は残りの幅いっぱいに広げて目立たせる。
                もどる と あそぶ はひとまとまりにして、いつも あそぶ の左に もどる がある。
                プレビューの列が 900 より狭いとき（16:10・4:3）は、このまとまりを 1 列目いっぱいにし、
                ほかは 2 列目に同じ幅で横いっぱいに並べて、1 列目と端をそろえる（判定はこの列の幅 = @container） */}
          <div
            data-guide="detail_actions"
            className="flex-row-el flex-wrap items-center w-full gap-x-4 gap-y-3 shrink-0 pb-2"
          >
            <div className="flex-row-el items-center flex-1 min-w-0 gap-4 @max-[900px]:basis-full">
              <BuilderPillButton
                color={PillColor.BLUE}
                size={PillSize.MD}
                icon="arrow_back"
                label={I18n.t("MSG_BACK_BTN")}
                ariaLabel="back"
                onClick={handleBack}
              />
              <BuilderPillButton
                color={PillColor.ORANGE}
                size={PillSize.MD}
                icon="play_arrow"
                label={I18n.t("MSG_BUILDER_BTN_PLAY")}
                ariaLabel="play"
                className="!flex-1"
                onClick={() => fire(PieceCardAction.PLAY)}
              />
            </div>

            {hasSecondaryActions && (
              <div className="flex-row-el items-center gap-4 @max-[900px]:basis-full">
                {context === PieceListContext.MINE && (
                  <BuilderPillButton
                    color={PillColor.GREEN}
                    size={PillSize.MD}
                    icon="edit"
                    label={I18n.t("MSG_BUILDER_MAP_EDIT_BTN")}
                    ariaLabel="edit"
                    className="@max-[900px]:!flex-1"
                    onClick={() => fire(PieceCardAction.EDIT)}
                  />
                )}

                {piece.enable_copied && (
                  <BuilderPillButton
                    color={PillColor.PURPLE}
                    size={PillSize.MD}
                    icon="cached"
                    label={I18n.t("MSG_BUILDER_MENU_COPY")}
                    ariaLabel="copy"
                    className="@max-[900px]:!flex-1"
                    onClick={() => fire(PieceCardAction.COPY)}
                  />
                )}

                {context === PieceListContext.PUBLIC && (
                  <BuilderPillButton
                    color={PillColor.WHITE_RED}
                    size={PillSize.MD}
                    icon="favorite"
                    iconFilled={liked}
                    label={likeCount}
                    ariaLabel="favorite"
                    className="@max-[900px]:!flex-1"
                    onClick={handleFavorite}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* タブのボード。ページの最小幅 1280 でもプレビューに 624 残るよう、600 を下限にする */}
        <div className="flex-col-el relative w-[40%] min-w-[600px] shrink-0 overflow-hidden bg-white rounded-3xl shadow-md">
          {/* 状態のバッジ（1 行目）と数字（2 行目）。どのタブでも見えるよう、タブの上に置く。
              1 行に混ぜると、画面の幅によって最後の 1 つだけが次の行にこぼれるので、行を分ける。
              大きさは下のボタンや名前と釣り合うよう、すべて高さ 48・type-body にそろえる */}
          <div className="flex-col-el items-start shrink-0 box-border w-full gap-2 px-4 pt-4">
            {(context === PieceListContext.MINE || publicPill) && (
              <div className="flex-row-el flex-wrap items-center gap-2">
                {context === PieceListContext.MINE && <PieceStatusBadges piece={piece} large />}
                {publicPill && (
                  <StatusBadge
                    icon={publicPill.icon}
                    labelKey={publicPill.labelKey}
                    pillClass={publicPill.pillClass}
                    textClass="text-white"
                    iconOnly={false}
                    large
                  />
                )}
              </div>
            )}
            <div className="flex-row-el flex-wrap items-center gap-2">
              {stats.map(({ key, icon, colorClass, filled, value }) => (
                <div
                  key={key}
                  className="flex-row-el items-center gap-1 h-12 px-4 rounded-full bg-beige-60"
                >
                  <span
                    className={`material-symbols-outlined ${filled ? "material-symbols-filled" : ""} type-md ${colorClass}`}
                  >
                    {icon}
                  </span>
                  <p className="type-text type-body text-textcolor-black">{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* タブバー。アイコンの下に文字（アイコンは文字の 2 倍: type-title / type-sm）。
              横に並べた形だと 4 つに type-body の文字が収まらない */}
          <div
            data-guide="detail_tabs"
            className="flex-row-el items-center shrink-0 box-border w-full gap-2 p-4"
          >
            {TAB_ITEMS.map(({ id, labelKey, icon }) => {
              const active = id === tab;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={handleSelectTab(id)}
                  className={`type-inherit-font flex-col-center flex-1 min-w-0 gap-1 h-20 box-border px-2 rounded-2xl border-solid border-2 cursor-pointer transition-colors ${
                    active
                      ? `${accentBg} ${accentBorder} text-white`
                      : "border-beige-60 bg-beige-60 text-textcolor-black/70"
                  }`}
                >
                  <span className="material-symbols-outlined type-title shrink-0">{icon}</span>
                  <p className="type-text type-sm min-w-0 truncate !whitespace-nowrap text-center">
                    {I18n.t(labelKey)}
                  </p>
                </button>
              );
            })}
          </div>

          {/* パネル */}
          <div
            id={GuideTargetId.DETAIL_BODY}
            className="flex-col-el items-start flex-1 min-h-0 box-border w-full gap-4 bg-white px-6 pb-6 overflow-y-auto invisible-scrollbar"
          >
            {tab === DetailTab.INFO && (
              <>
                {piece.description && (
                  <p className="type-text-left type-body text-textcolor-black w-full">
                    {piece.description}
                  </p>
                )}

                {/* きもち：押せる大きなボタンを横に 6 つ */}
                <div data-guide="detail_reaction" className="flex-col-el items-start w-full gap-2">
                  {/* 押せるときは押せることを、押せないとき（自分のピース）は何の数かを、ひとこと添える */}
                  {canReact ? (
                    <div className="flex-row-el items-center gap-1">
                      <span className="material-symbols-outlined type-md text-blue-100">
                        touch_app
                      </span>
                      <p className="type-text-left type-sm text-textcolor-black/70">
                        {I18n.t("MSG_BUILDER_DETAIL_REACTION_HINT")}
                      </p>
                    </div>
                  ) : (
                    <div className="flex-row-el items-center gap-1">
                      <span className="material-symbols-outlined material-symbols-filled type-md text-blue-100">
                        mood
                      </span>
                      <p className="type-text-left type-md text-textcolor-black">
                        {I18n.t("MSG_BUILDER_DETAIL_REACTION_TITLE")}
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-6 gap-2 w-full">
                    {REACTIONS.map(({ key, icon }) =>
                      canReact ? (
                        <button
                          key={key}
                          type="button"
                          onClick={handleReact(key)}
                          aria-label={`reaction-${key}`}
                          className={`type-inherit-font flex-col-center gap-1 h-24 box-border rounded-2xl border-solid border-2 cursor-pointer transition-transform active:scale-95 ${
                            myReactions.includes(key)
                              ? "bg-blue-100/20 border-blue-100"
                              : "bg-white border-blue-60"
                          }`}
                        >
                          <div className="flex-col-center w-10 h-10">{icon}</div>
                          <p className="type-text type-body text-textcolor-black">
                            {reactionCounts[key]}
                          </p>
                        </button>
                      ) : (
                        <div
                          key={key}
                          className="flex-col-center gap-1 h-24 box-border rounded-2xl border-solid border-2 border-beige-60 bg-white"
                        >
                          <div className="flex-col-center w-10 h-10">{icon}</div>
                          <p className="type-text type-body text-textcolor-black">
                            {reactionCounts[key]}
                          </p>
                        </div>
                      ),
                    )}
                  </div>
                </div>

                {/* ヒント：やさしい・ふつう・こたえ を横に 3 枚（中身に合わせた高さ。3 枚は同じ高さにそろえる） */}
                <div className="flex-col-el items-start w-full gap-2">
                  <div className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined material-symbols-filled type-md text-orange-100">
                      lightbulb
                    </span>
                    <p className="type-text-left type-md text-textcolor-black">
                      {I18n.t("MSG_BUILDER_DETAIL_HINT_SECTION")}
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 w-full items-stretch">
                    {HINT_LEVELS.map((lv, i) => (
                      <div
                        key={lv}
                        className="flex-col-el items-center justify-center gap-2 min-h-24 box-border rounded-2xl p-4 bg-orange-10"
                      >
                        <p className="type-text type-md text-orange-100">{I18n.t(lv)}</p>
                        {hints[i] ? (
                          <p className="type-text-left type-body w-full break-keep text-textcolor-black">
                            {hints[i]}
                          </p>
                        ) : (
                          <p className="type-text type-body break-keep text-textcolor-black/50">
                            {I18n.t("MSG_BUILDER_DETAIL_HINT_EMPTY")}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* 日付（ボードのいちばん下に小さく） */}
                <div className="flex-row-el flex-wrap items-center gap-x-4 gap-y-1 w-full mt-auto">
                  <div className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined type-body text-textcolor-black/50">
                      public
                    </span>
                    <p className="type-text type-sm text-textcolor-black/60">
                      {I18n.t("MSG_BUILDER_DETAIL_PUBLISHED_AT")}
                    </p>
                    <p className="type-text type-sm text-textcolor-black/60">
                      {piece.published_at ? dayjs(piece.published_at).format("YYYY/MM/DD") : "-"}
                    </p>
                  </div>
                  <div className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined type-body text-textcolor-black/50">
                      update
                    </span>
                    <p className="type-text type-sm text-textcolor-black/60">
                      {I18n.t("MSG_BUILDER_DETAIL_UPDATED_AT")}
                    </p>
                    <p className="type-text type-sm text-textcolor-black/60">
                      {dayjs(piece.updated_at).format("YYYY/MM/DD")}
                    </p>
                  </div>
                </div>
              </>
            )}
            {tab === DetailTab.RANKING && (
              <>
                {ranking.length === 0 ? (
                  <p className="type-text type-sm text-textcolor-black/70">
                    {I18n.t("MSG_BUILDER_DETAIL_RANKING_EMPTY")}
                  </p>
                ) : (
                  <>
                    <div className="flex-row-el items-center justify-between w-full rounded-xl px-4 py-3 bg-yellow-100">
                      <div className="flex-col-el items-start">
                        <p className="type-text type-sm text-textcolor-black/70">
                          {I18n.t("MSG_BUILDER_DETAIL_RANKING_WORLD")}
                        </p>
                        <p className="type-text type-title text-textcolor-black">
                          {I18n.t("MSG_BUILDER_DETAIL_RANKING_MOVES_UNIT", {
                            n: ranking[0].moves,
                          })}
                        </p>
                      </div>
                      <p className="type-text type-title">👑</p>
                    </div>

                    <div className="flex-col-el w-full gap-2">
                      {ranking.map((r) => (
                        <div
                          key={r.uid}
                          className={`flex-row-el items-center gap-2 w-full rounded-xl px-3 py-2 ${
                            r.uid === active?.uid
                              ? "border-solid border-2 border-yellow-100 bg-yellow-100/30"
                              : "bg-beige-60"
                          }`}
                        >
                          <p className="type-text type-sm">{RANK_MEDALS[r.rank - 1] ?? ""}</p>
                          <p className="type-text type-sm text-textcolor-black w-6 text-center">
                            {r.rank}
                          </p>
                          <div className="flex-col-center shrink-0">
                            <UserAvatar size={32} icon={r.icon ?? ""} />
                          </div>
                          <p className="type-text type-sm text-textcolor-black flex-1 text-left truncate">
                            {r.uid === active?.uid
                              ? I18n.t("MSG_BUILDER_DETAIL_RANKING_YOU")
                              : (r.name ?? "")}
                          </p>
                          <p className="type-text type-sm text-textcolor-black whitespace-nowrap">
                            {I18n.t("MSG_BUILDER_DETAIL_RANKING_MOVES_UNIT", { n: r.moves })}
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {tab === DetailTab.COMMAND && (
              <MapDetailCommandPanel enabledCommands={new Set(DEFAULT_ENABLED_COMMANDS)} />
            )}

            {tab === DetailTab.COMMENT && (
              <MapDetailCommentPanel
                pieceId={piece.piece_id}
                piecePublished={piece.published}
                pieceCommentEnabled={piece.enable_comment}
              />
            )}
          </div>
        </div>
      </div>

      {/* 通報確認 */}
      {/* ゲストは通報できない。押した時点で理由を出す */}
      <BuilderAccountRequiredDialog
        visible={showReportDenied}
        accent={BuilderModalAccent.ORANGE}
        icon="flag"
        title={I18n.t("MSG_BUILDER_MENU_REPORT")}
        message={I18n.t("MSG_BUILDER_REPORT_NEED_ACCOUNT")}
        close={() => setShowReportDenied(false)}
      />

      <BuilderReportDialog
        visible={reportOpen}
        title={I18n.t("MSG_BUILDER_MENU_REPORT")}
        target={piece.name}
        message={I18n.t("MSG_BUILDER_REPORT_PIECE_CONFIRM")}
        close={() => setReportOpen(false)}
        handleConfirm={(reason, detail) => {
          setReportOpen(false);
          if (!active?.uid) return;
          reportPiece({ piece_id: piece.piece_id, uid: active.uid, reason, detail })
            .then(() =>
              toast.success(
                <div>
                  <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_REPORT_THANKS")}</p>
                </div>,
              ),
            )
            .catch((error) =>
              toast.warn(
                <div>
                  <p className="text text-textcolor-black">
                    {I18n.t(
                      axios.isAxiosError(error) && error.response?.status === 403
                        ? "MSG_BUILDER_REPORT_NEED_ACCOUNT"
                        : "MSG_BUILDER_ACTION_FAILED",
                    )}
                  </p>
                </div>,
              ),
            );
        }}
      />
    </div>
  );
};
